/**
 * MemorizProgress widget -- Memorization progress tracker.
 */

import type { Widget, WidgetContext } from '../types';

interface MemorizData {
  surahsStarted: number;
  ayahsMemorized: number;
  currentSurah: string;
  currentProgress: number;
  reviewStreak: number;
  nextReviewDue: string;
  cardsDue: number;
}

function getMockData(): MemorizData {
  try {
    const raw = localStorage.getItem('aweni_memoriz_progress');
    if (raw) return JSON.parse(raw);
  } catch { /* */ }
  return {
    surahsStarted: 5,
    ayahsMemorized: 87,
    currentSurah: 'Al-Baqarah',
    currentProgress: 45,
    reviewStreak: 12,
    nextReviewDue: '2h',
    cardsDue: 5,
  };
}

export const MemorizProgress: Widget = {
  id: 'memoriz-progress',
  label: { en: 'Memorization', fr: 'Memorisation', ar: 'الحفظ', es: 'Memorizacion', de: 'Auswendiglernen' },
  icon: '📚',
  defaultVisible: true,
  defaultOrder: 7,
  defaultSize: 'compact',

  visibilityRule: () => {
    try {
      const user = JSON.parse(localStorage.getItem('aweni_user') || '{}');
      return user.prayerEnabled !== false;
    } catch { return true; }
  },

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const data = getMockData();
    const totalAyahs = 6236;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for future expanded layout
    const ayahProgress = Math.round((data.ayahsMemorized / totalAyahs) * 100 * 10) / 10;

    if (size === 'compact') {
      container.innerHTML = `
        <div class="rounded-2xl p-4 cursor-pointer" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="memoriz">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span>📚</span>
              <span class="text-sm" style="color:var(--text-primary);">
                ${ctx.t('widget_memorizing')}: ${data.currentSurah} · ${data.currentProgress}%
              </span>
            </div>
            <span class="text-xs" style="color:var(--text-secondary);">${data.cardsDue} ${ctx.t('widget_cards_due_short')}</span>
          </div>
        </div>`;
    } else {
      container.innerHTML = `
        <div class="rounded-2xl p-5 cursor-pointer" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="memoriz">
          <h3 class="text-sm font-semibold uppercase tracking-wide mb-3" style="color:var(--text-secondary);">${ctx.t('widget_memoriz_title')}</h3>

          <div class="grid grid-cols-2 gap-3 mb-3">
            <div class="rounded-xl p-3" style="background:var(--bg-secondary);">
              <p class="text-xs" style="color:var(--text-secondary);">${ctx.t('widget_surahs_started')}</p>
              <p class="text-lg font-bold" style="color:var(--text-primary);">${data.surahsStarted}/114</p>
            </div>
            <div class="rounded-xl p-3" style="background:var(--bg-secondary);">
              <p class="text-xs" style="color:var(--text-secondary);">${ctx.t('widget_ayahs_memorized')}</p>
              <p class="text-lg font-bold" style="color:var(--text-primary);">${data.ayahsMemorized}/${totalAyahs}</p>
            </div>
          </div>

          <div class="mb-3">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs font-medium" style="color:var(--text-primary);">${data.currentSurah}</span>
              <span class="text-xs" style="color:var(--text-secondary);">${data.currentProgress}%</span>
            </div>
            <div class="rounded-full overflow-hidden" style="height:6px;background:var(--bg-secondary);">
              <div class="rounded-full" style="height:100%;width:${data.currentProgress}%;background:#F59E0B;transition:width var(--motion-duration-slow);"></div>
            </div>
          </div>

          <div class="flex items-center justify-between text-xs">
            <span style="color:var(--text-secondary);">🔥 ${ctx.t('widget_review_streak')}: ${data.reviewStreak} ${ctx.t('widget_days')}</span>
            <span style="color:var(--accent);">${data.cardsDue} ${ctx.t('widget_cards_due_short')} ${ctx.t('widget_in_time', { time: data.nextReviewDue })}</span>
          </div>
        </div>`;
    }

    container.querySelectorAll<HTMLElement>('[data-nav]').forEach((el) => {
      el.addEventListener('click', () => ctx.navigate('memoriz'));
    });
  },
};
